import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapPin, X, ArrowRight } from "lucide-react";

const DISMISS_KEY = "dinard_cta_dismissed_until";
const DISMISS_DAYS = 7;
const SCROLL_THRESHOLD_RATIO = 0.3; // 30 % du document

/**
 * Mini-CTA sticky "Étude gratuite sous 24 h", affiché uniquement sur :
 *   - /villes/dinard
 *   - /blog/<slug contenant "dinard">
 *
 * Apparition au scroll > 30 %, bouton × qui mémorise la fermeture 7 jours.
 */
const DinardStickyCTA = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [eligible, setEligible] = useState(false);

  // 1. Eligibilité de la page (Dinard uniquement)
  useEffect(() => {
    const path = location.pathname.toLowerCase();
    const isDinardCity = path === "/villes/dinard";
    const isDinardBlog = path.startsWith("/blog/") && path.includes("dinard");
    setEligible(isDinardCity || isDinardBlog);
    setVisible(false); // reset à chaque navigation
  }, [location.pathname]);

  // 2. Mémorisation 7 jours (localStorage)
  const isDismissed = () => {
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      return until > Date.now();
    } catch {
      return false;
    }
  };

  // 3. Scroll listener, apparait après 30 % de scroll
  useEffect(() => {
    if (!eligible || isDismissed()) return;
    const onScroll = () => {
      const scrolled = window.scrollY;
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll <= 0) return;
      const ratio = scrolled / maxScroll;
      if (ratio > SCROLL_THRESHOLD_RATIO) setVisible(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [eligible, location.pathname]);

  const handleDismiss = (e) => {
    e.stopPropagation();
    try {
      const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISS_KEY, String(until));
    } catch {
      /* ignore quota errors */
    }
    setVisible(false);
  };

  const handleClick = () => {
    navigate("/contact", { state: { prefill_ville: "Dinard" } });
  };

  if (!eligible || !visible) return null;

  return (
    <div
      data-testid="dinard-sticky-cta"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-[640px] animate-[slideUp_0.35s_ease-out]"
      style={{
        animation: "slideUp 0.35s ease-out",
      }}
    >
      <div className="relative flex items-center gap-3 bg-[#0F2A3D] text-white rounded-2xl shadow-2xl pl-4 pr-12 py-3 sm:pl-5 sm:pr-14 sm:py-4 border border-white/10 backdrop-blur-md">
        <div className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#D4A574]/15 flex items-center justify-center">
          <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4A574]" strokeWidth={2} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-[11px] sm:text-xs uppercase tracking-wider text-[#D4A574] font-semibold">
            Propriétaire à Dinard ?
          </div>
          <div className="text-sm sm:text-base font-medium leading-tight mt-0.5 truncate sm:whitespace-normal">
            Étude personnalisée gratuite sous 24 h
          </div>
        </div>

        <button
          type="button"
          onClick={handleClick}
          data-testid="dinard-sticky-cta-button"
          className="hidden sm:inline-flex items-center gap-1.5 bg-[#D4A574] hover:bg-[#C4945F] text-[#0F2A3D] font-semibold text-sm px-4 py-2 rounded-full transition-colors flex-shrink-0"
        >
          Je veux mon étude
          <ArrowRight className="w-4 h-4" />
        </button>

        {/* On mobile, the whole card is clickable */}
        <button
          type="button"
          onClick={handleClick}
          data-testid="dinard-sticky-cta-mobile-overlay"
          aria-label="Demander une étude gratuite sous 24 heures à Dinard"
          className="sm:hidden absolute inset-0 rounded-2xl"
        />

        <button
          type="button"
          onClick={handleDismiss}
          data-testid="dinard-sticky-cta-close"
          aria-label="Fermer"
          className="absolute right-2 top-2 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translate(-50%, 20px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
};

export default DinardStickyCTA;
